import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowDownCircle,
  Clock,
  CheckCircle2,
  XCircle,
  CreditCard,
  ShieldCheck,
  Globe2,
  Sparkles,
  ArrowUpRight,
  TrendingUp,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCountUp } from "@/hooks/use-count-up";
import { useTranslation } from "react-i18next";
import { useT } from "@/i18n/useT";
import { WithdrawDialog } from "@/components/WithdrawDialog";

export const Route = createFileRoute("/_authenticated/wallet")({
  head: () => ({
    meta: [
      { title: "Wallet — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WalletPage,
});

type WStatus = "pending" | "approved" | "paid" | "rejected";

interface WithdrawalRow {
  id: string;
  amount_usd: number | null;
  amount: number;
  amount_local: number | null;
  local_currency: string | null;
  exchange_rate_used: number | null;
  country: string | null;
  method: string;
  destination: string;
  status: WStatus;
  admin_note: string | null;
  tx_reference: string | null;
  created_at: string;
  paid_at: string | null;
}

function WalletPage() {
  const { t, i18n } = useTranslation();
  const tt = useT();
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const isRtl = i18n.dir() === "rtl";

  const isFreelancer = profile?.role === "freelancer";

  const { data: balance } = useQuery({
    queryKey: ["wallet-balance", user?.id],
    enabled: !!user && isFreelancer,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("freelancer_balance", { _user_id: user!.id });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return {
        released: Number(row?.released ?? 0),
        requested: Number(row?.requested ?? 0),
        available: Number(row?.available ?? 0),
      };
    },
  });

  const { data: escrow } = useQuery({
    queryKey: ["wallet-escrow", user?.id],
    enabled: !!user && isFreelancer,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("amount, net_amount, stripe_fee_amount")
        .eq("freelancer_id", user!.id)
        .eq("status", "paid_to_platform");
      if (error) throw error;
      const total = (data ?? []).reduce(
        (sum, r: { amount: number | null; net_amount: number | null }) =>
          sum + Number(r.net_amount ?? r.amount ?? 0),
        0,
      );
      return { total, count: data?.length ?? 0 };
    },
  });

  const { data: withdrawals, isLoading } = useQuery({
    queryKey: ["withdrawals", user?.id],
    enabled: !!user && isFreelancer,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("withdrawals")
        .select(
          "id, amount, amount_usd, amount_local, local_currency, exchange_rate_used, country, method, destination, status, admin_note, tx_reference, created_at, paid_at",
        )
        .eq("freelancer_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as WithdrawalRow[];
    },
  });

  if (!isFreelancer) {
    return (
      <div className="mx-auto max-w-lg rounded-3xl border bg-card p-8 text-center text-sm text-muted-foreground">
        {tt("The wallet is available for freelancer accounts.", "المحفظة متاحة لحسابات المستقلين فقط.")}
      </div>
    );
  }

  const availableTotal = balance?.available ?? 0;
  const escrowTotal = escrow?.total ?? 0;
  const releasedTotal = balance?.released ?? 0;
  const requestedTotal = balance?.requested ?? 0;
  const canRequest = availableTotal > 0 && profile?.kyc_status === "verified";

  const availableShown = useCountUp(availableTotal);

  return (
    <div className="relative space-y-10 pb-6">
      {/* Ambient light — Apple hardware backdrop */}
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/4 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/[0.08] blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute top-40 right-0 h-96 w-96 rounded-full bg-indigo-500/[0.05] blur-[120px]" />

      {/* ================= Hero balance card ================= */}
      <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-8 shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_20px_60px_-20px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:p-10">
        {/* Specular highlight */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/15 blur-[80px]" />
        <div aria-hidden className="pointer-events-none absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-fuchsia-500/[0.06] blur-[100px]" />

        <div className="relative grid gap-8 md:grid-cols-[1.35fr_1fr] md:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground backdrop-blur-sm">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              {tt("Available balance", "الرصيد المتاح")}
            </div>

            <div className="mt-5 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-muted-foreground/70">$</span>
              <span
                className="font-display text-6xl font-semibold tracking-tight text-foreground tabular-nums sm:text-7xl"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {availableShown.toFixed(2)}
              </span>
            </div>

            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              {tt(
                "Track your earnings after Stripe fees and withdraw to your local bank or BaridiMob within 24–48 hours.",
                "تتبع أرباحك بعد رسوم سترايب واسحبها إلى بنكك المحلي أو BaridiMob خلال 24–48 ساعة.",
              )}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => setOpen(true)}
                disabled={!canRequest}
                size="lg"
                className="group h-11 rounded-full px-6 text-sm font-semibold shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.7)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_hsl(var(--primary)/0.9)]"
              >
                <ArrowDownCircle className="h-4 w-4" />
                {tt("Request payout", "طلب سحب")}
                <ArrowUpRight
                  className={cn(
                    "h-4 w-4 opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
                    isRtl && "-scale-x-100",
                  )}
                />
              </Button>
              {!canRequest && profile?.kyc_status !== "verified" && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/[0.06] px-3 py-1.5 text-[11px] font-medium text-amber-500">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {tt("Verify identity to enable payouts", "تحقق من الهوية لتفعيل السحب")}
                </span>
              )}
            </div>
          </div>

          {/* Right-side compact stack */}
          <div className="relative grid grid-cols-1 gap-3">
            <MiniStat
              label={tt("Protected", "محمية")}
              value={escrowTotal}
              icon={Clock}
              tone="amber"
              caption={
                (escrow?.count ?? 0) > 0
                  ? tt(`${escrow?.count} invoice(s) locked`, `${escrow?.count} فاتورة مقفلة`)
                  : tt("No pending invoices", "لا فواتير معلقة")
              }
            />
            <MiniStat
              label={t("wallet.stats.releasedToYou")}
              value={releasedTotal}
              icon={TrendingUp}
              tone="emerald"
              caption={tt("Lifetime earnings, net of fees", "الأرباح الإجمالية بعد الرسوم")}
            />
            <MiniStat
              label={t("wallet.stats.requestedPaid")}
              value={requestedTotal}
              icon={Sparkles}
              tone="indigo"
              caption={tt("Pending + paid withdrawals", "السحوبات المعلقة والمدفوعة")}
            />
          </div>
        </div>
      </section>

      {/* ================= Info strip ================= */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-rose-500/[0.05] to-transparent p-5 transition-colors hover:border-rose-500/25">
          <div aria-hidden className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-rose-500/10 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500/25 to-rose-500/5 ring-1 ring-rose-500/20">
              <CreditCard className="h-4 w-4 text-rose-400" />
            </div>
            <div className="min-w-0 space-y-1">
              <h4 className="text-sm font-semibold text-foreground">
                {tt("Card processing fee", "رسوم معالجة البطاقة")}
              </h4>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {tt("When clients pay with a card, ", "عند دفع العملاء ببطاقة، تُخصم ")}
                <span className="font-semibold text-rose-400">2.9% + 30¢</span>
                {tt(
                  " is deducted from each invoice. You receive the remainder protected and available balance.",
                  " من كل فاتورة. تستلم الباقي محمية والرصيد المتاح.",
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-primary/[0.06] to-transparent p-5 transition-colors hover:border-primary/25">
          <div aria-hidden className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 ring-1 ring-primary/20">
              <Globe2 className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-foreground">
                  {tt("Country-based payouts", "مدفوعات حسب الدولة")}
                </h4>
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  disabled={!canRequest}
                  className="shrink-0 text-[11px] font-medium text-primary hover:underline disabled:opacity-40"
                >
                  {tt("Start payout →", "ابدأ السحب →")}
                </button>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {tt("Algeria uses ", "الجزائر تستخدم ")}
                <span className="font-semibold text-yellow-400">BaridiMob (RIP)</span>
                {tt(
                  "; other countries use a local bank transfer at the live rate.",
                  "؛ الدول الأخرى تستخدم تحويلاً بنكياً بسعر الصرف اللحظي.",
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {withdrawals?.some((w) => w.status === "pending" || w.status === "approved") && (
        <div className="relative flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 text-sm backdrop-blur-sm">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="font-medium text-foreground">{tt("Withdrawal in progress", "السحب قيد المعالجة")}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {tt("Payouts are processed manually. Expect funds within ", "تتم معالجة المدفوعات يدوياً. تصل الأموال خلال ")}
              <strong className="text-foreground">{tt("24–48 hours", "24–48 ساعة")}</strong>
              {tt(". You'll get an email once sent.", ". ستصلك رسالة بريد إلكتروني بمجرد الإرسال.")}
            </div>
          </div>
        </div>
      )}

      {/* ================= History ================= */}
      <section className="relative space-y-4">
        <div className="flex items-end justify-between px-1">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            {t("wallet.historyLabel")}
          </h2>
          {withdrawals && withdrawals.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              {withdrawals.length} {tt("total", "إجمالي")}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] py-16 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : !withdrawals || withdrawals.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/[0.02] px-6 py-16 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.03] ring-1 ring-white/10">
              <ArrowDownCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">{t("wallet.empty")}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl">
            {/* Desktop table */}
            <div className="hidden sm:block">
              <table className="w-full text-start text-sm">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.015]">
                    <th className="px-6 py-4 text-start text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{tt("Requested", "التاريخ")}</th>
                    <th className="px-6 py-4 text-start text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{tt("Amount", "المبلغ")}</th>
                    <th className="px-6 py-4 text-start text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{tt("Local", "محلي")}</th>
                    <th className="px-6 py-4 text-start text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{tt("Method", "الطريقة")}</th>
                    <th className="px-6 py-4 text-end text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{tt("Status", "الحالة")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {withdrawals.map((w, i) => {
                    const usd = Number(w.amount_usd ?? w.amount);
                    const local = w.amount_local ? Number(w.amount_local) : null;
                    return (
                      <tr
                        key={w.id}
                        className="animate-fade-in transition-colors hover:bg-white/[0.02]"
                        style={{ animationDelay: `${i * 40}ms` }}
                      >
                        <td className="px-6 py-5 text-xs text-muted-foreground">
                          {new Date(w.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-5 font-mono text-sm font-semibold text-foreground tabular-nums">
                          ${usd.toFixed(2)}
                        </td>
                        <td className="px-6 py-5 font-mono text-xs text-muted-foreground">
                          {local != null ? `${local.toLocaleString()} ${w.local_currency ?? ""}` : "—"}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-medium text-foreground">
                              {w.method === "baridimob"
                                ? "BaridiMob"
                                : w.method === "bank_transfer"
                                ? tt("Bank transfer", "تحويل بنكي")
                                : w.method}
                            </span>
                            {w.country && (
                              <span className="text-muted-foreground">· {w.country}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col items-end gap-1">
                            <StatusPill status={w.status} />
                            {(w.status === "pending" || w.status === "approved") && (
                              <span className="text-[10px] text-muted-foreground">
                                {tt("ETA: 24–48h", "الوصول: 24–48 ساعة")}
                              </span>
                            )}
                            {w.paid_at && (
                              <span className="text-[10px] text-muted-foreground">
                                {tt("Paid", "تم الدفع")} {new Date(w.paid_at).toLocaleDateString()}
                              </span>
                            )}
                            {w.admin_note && (
                              <span className="max-w-[220px] truncate text-[10px] italic text-muted-foreground">
                                {w.admin_note}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div className="divide-y divide-white/5 sm:hidden">
              {withdrawals.map((w, i) => {
                const usd = Number(w.amount_usd ?? w.amount);
                const local = w.amount_local ? Number(w.amount_local) : null;
                return (
                  <div
                    key={w.id}
                    className="animate-fade-in p-5"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-lg font-semibold text-foreground tabular-nums">
                          ${usd.toFixed(2)}
                        </div>
                        {local != null && (
                          <div className="font-mono text-xs text-muted-foreground">
                            {local.toLocaleString()} {w.local_currency ?? ""}
                          </div>
                        )}
                      </div>
                      <StatusPill status={w.status} />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {w.method === "baridimob"
                          ? "BaridiMob"
                          : w.method === "bank_transfer"
                          ? tt("Bank transfer", "تحويل بنكي")
                          : w.method}
                      </span>
                      {w.country && <span>· {w.country}</span>}
                      <span>·</span>
                      <span>{new Date(w.created_at).toLocaleDateString()}</span>
                    </div>
                    {w.admin_note && (
                      <p className="mt-2 text-[11px] italic text-muted-foreground">{w.admin_note}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <WithdrawDialog open={open} onOpenChange={setOpen} availableUsd={availableTotal} />
    </div>
  );
}

function MiniStat({
  label,
  value,
  caption,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  caption?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: "amber" | "emerald" | "indigo";
}) {
  const shown = useCountUp(value);
  const toneCls = {
    amber: {
      ring: "ring-amber-500/20",
      bg: "from-amber-500/20 to-amber-500/5",
      text: "text-amber-400",
    },
    emerald: {
      ring: "ring-emerald-500/20",
      bg: "from-emerald-500/20 to-emerald-500/5",
      text: "text-emerald-400",
    },
    indigo: {
      ring: "ring-indigo-500/20",
      bg: "from-indigo-500/20 to-indigo-500/5",
      text: "text-indigo-400",
    },
  }[tone];

  return (
    <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3.5 backdrop-blur-sm transition-all hover:border-white/20 hover:bg-white/[0.04]">
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ring-1",
          toneCls.bg,
          toneCls.ring,
        )}
      >
        <Icon className={cn("h-4 w-4", toneCls.text)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </div>
        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-xs text-muted-foreground/70">$</span>
          <span className="font-mono text-lg font-semibold tabular-nums text-foreground">
            {shown.toFixed(2)}
          </span>
        </div>
        {caption && (
          <div className="mt-0.5 truncate text-[10px] text-muted-foreground">{caption}</div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: WStatus }) {
  const tt = useT();
  const map: Record<WStatus, { label: string; icon: typeof Clock; cls: string; ring: string; dot: string }> = {
    pending: {
      label: tt("Pending", "قيد الانتظار"),
      icon: Clock,
      cls: "text-amber-400 bg-amber-500/10",
      ring: "ring-amber-500/20",
      dot: "bg-amber-400 shadow-[0_0_8px_rgb(251_191_36_/_0.9)]",
    },
    approved: {
      label: tt("Approved", "تمت الموافقة"),
      icon: CheckCircle2,
      cls: "text-primary bg-primary/10",
      ring: "ring-primary/25",
      dot: "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.9)]",
    },
    paid: {
      label: tt("Paid", "مدفوعة"),
      icon: CheckCircle2,
      cls: "text-emerald-400 bg-emerald-500/10",
      ring: "ring-emerald-500/20",
      dot: "bg-emerald-400 shadow-[0_0_8px_rgb(52_211_153_/_0.9)]",
    },
    rejected: {
      label: tt("Rejected", "مرفوضة"),
      icon: XCircle,
      cls: "text-rose-400 bg-rose-500/10",
      ring: "ring-rose-500/20",
      dot: "bg-rose-400 shadow-[0_0_8px_rgb(251_113_133_/_0.9)]",
    },
  };
  const s = map[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1",
        s.cls,
        s.ring,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
      {s.label}
    </span>
  );
}
